import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { AlertCircle, ArrowLeft, Copy, KeyRound, Plus, RefreshCw, X } from 'lucide-react'
import { useId, useState } from 'react'
import {
  type AdminUser,
  type AdminUserListFilter,
  type CreateAdminUserInput,
  createAdminUser,
  listAdminUsers,
  resetAdminUserPassword,
  type UpdateAdminUserInput,
  type UserStatus,
  updateAdminUser,
} from '@/api/admin'
import type { UserRole } from '@/api/auth'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { describeErrorMessage } from '@/lib/errors'
import { cn } from '@/lib/utils'

export const Route = createFileRoute('/admin/users')({
  component: AdminUsersPage,
})

const ROLE_LABEL: Record<UserRole, string> = {
  seller: 'Продавец',
  coach: 'Тренер',
  admin: 'Админ',
}

const STATUS_LABEL: Record<UserStatus, string> = {
  active: 'Активен',
  disabled: 'Заблокирован',
}

function AdminUsersPage() {
  const [filter, setFilter] = useState<AdminUserListFilter>({ limit: 100 })
  const [createOpen, setCreateOpen] = useState(false)
  const [editUser, setEditUser] = useState<AdminUser | null>(null)
  const [resetFor, setResetFor] = useState<AdminUser | null>(null)
  const [resetResult, setResetResult] = useState<{ user: AdminUser; password: string } | null>(null)
  const [createdResult, setCreatedResult] = useState<{ user: AdminUser; password: string } | null>(
    null,
  )

  const query = useQuery({
    queryKey: ['admin', 'users', filter],
    queryFn: () => listAdminUsers(filter),
  })
  const queryClient = useQueryClient()

  const createMutation = useMutation({
    mutationFn: createAdminUser,
    onSuccess: (result) => {
      setCreateOpen(false)
      setCreatedResult({ user: result.user, password: result.tempPassword })
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] })
    },
  })

  const updateMutation = useMutation({
    mutationFn: (payload: { id: string; input: UpdateAdminUserInput }) =>
      updateAdminUser(payload.id, payload.input),
    onSuccess: () => {
      setEditUser(null)
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] })
    },
  })

  const resetMutation = useMutation({
    mutationFn: (user: AdminUser) => resetAdminUserPassword(user.id),
    onSuccess: (password, user) => {
      setResetFor(null)
      setResetResult({ user, password })
    },
  })

  return (
    <main className="mx-auto flex w-full max-w-6xl flex-col gap-4 px-4 py-6 pb-12">
      <Link
        to="/admin"
        className="-ml-3 inline-flex w-fit items-center gap-2 rounded-md px-3 py-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden="true" />В админ-панель
      </Link>

      <header className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-col gap-1">
          <h1 className="text-2xl font-semibold tracking-tight sm:text-3xl">Пользователи</h1>
          <p className="text-sm text-muted-foreground">
            {query.data ? `Всего: ${query.data.total}` : 'Загружаем список…'}
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)}>
          <Plus className="h-4 w-4" aria-hidden="true" />
          Добавить
        </Button>
      </header>

      <UsersFilterBar filter={filter} onChange={setFilter} />

      {query.isLoading ? (
        <StatusCard title="Загружаем пользователей" />
      ) : query.isError ? (
        <StatusCard
          title="Не удалось загрузить"
          description={describeErrorMessage(query.error, 'Попробуйте обновить.')}
        />
      ) : (
        <UsersTable
          users={query.data?.items ?? []}
          onEdit={setEditUser}
          onResetPassword={setResetFor}
        />
      )}

      {createOpen && (
        <CreateUserDialog
          onClose={() => setCreateOpen(false)}
          onSubmit={(input) => createMutation.mutate(input)}
          pending={createMutation.isPending}
          error={createMutation.error}
        />
      )}
      {editUser && (
        <EditUserDialog
          user={editUser}
          onClose={() => setEditUser(null)}
          onSubmit={(input) => updateMutation.mutate({ id: editUser.id, input })}
          pending={updateMutation.isPending}
          error={updateMutation.error}
        />
      )}
      {resetFor && (
        <ConfirmResetDialog
          user={resetFor}
          onCancel={() => setResetFor(null)}
          onConfirm={() => resetMutation.mutate(resetFor)}
          pending={resetMutation.isPending}
          error={resetMutation.error}
        />
      )}
      {createdResult && (
        <TempPasswordDialog
          title="Пользователь создан"
          description={`Пароль выдан «${createdResult.user.fullName}». Покажите его один раз и попросите сменить.`}
          password={createdResult.password}
          onClose={() => setCreatedResult(null)}
        />
      )}
      {resetResult && (
        <TempPasswordDialog
          title="Пароль сброшен"
          description={`Новый временный пароль для «${resetResult.user.fullName}».`}
          password={resetResult.password}
          onClose={() => setResetResult(null)}
        />
      )}
    </main>
  )
}

interface UsersFilterBarProps {
  filter: AdminUserListFilter
  onChange: (next: AdminUserListFilter) => void
}

function UsersFilterBar({ filter, onChange }: UsersFilterBarProps) {
  const queryId = useId()
  const roleId = useId()
  const statusId = useId()

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_auto_auto]">
      <div className="flex flex-col gap-1">
        <Label htmlFor={queryId}>Поиск по ФИО или логину</Label>
        <Input
          id={queryId}
          value={filter.query ?? ''}
          onChange={(e) => onChange({ ...filter, query: e.target.value, offset: 0 })}
          placeholder="Иванов или ivanov"
        />
      </div>
      <div className="flex flex-col gap-1">
        <Label htmlFor={roleId}>Роль</Label>
        <select
          id={roleId}
          className="h-9 rounded-md border border-input bg-background px-3 text-sm"
          value={filter.role ?? ''}
          onChange={(e) => onChange({ ...filter, role: (e.target.value as UserRole) || '' })}
        >
          <option value="">Все</option>
          <option value="seller">Продавец</option>
          <option value="coach">Тренер</option>
          <option value="admin">Админ</option>
        </select>
      </div>
      <div className="flex flex-col gap-1">
        <Label htmlFor={statusId}>Статус</Label>
        <select
          id={statusId}
          className="h-9 rounded-md border border-input bg-background px-3 text-sm"
          value={filter.status ?? ''}
          onChange={(e) => onChange({ ...filter, status: (e.target.value as UserStatus) || '' })}
        >
          <option value="">Все</option>
          <option value="active">Активные</option>
          <option value="disabled">Заблокированные</option>
        </select>
      </div>
    </div>
  )
}

interface UsersTableProps {
  users: AdminUser[]
  onEdit: (u: AdminUser) => void
  onResetPassword: (u: AdminUser) => void
}

function UsersTable({ users, onEdit, onResetPassword }: UsersTableProps) {
  if (users.length === 0) {
    return <StatusCard title="Пользователей не найдено" description="Скорректируйте фильтры." />
  }
  return (
    <div className="overflow-x-auto rounded-lg border">
      <table className="w-full text-sm">
        <thead className="bg-muted/50">
          <tr className="text-left">
            <th className="px-4 py-3 font-medium">ФИО</th>
            <th className="px-4 py-3 font-medium">Логин</th>
            <th className="px-4 py-3 font-medium">Роль</th>
            <th className="px-4 py-3 font-medium">Статус</th>
            <th className="px-4 py-3 font-medium">Магазины</th>
            <th className="px-4 py-3 text-right font-medium">Действия</th>
          </tr>
        </thead>
        <tbody>
          {users.map((user) => (
            <tr key={user.id} className="border-t">
              <td className="px-4 py-3 font-medium">{user.fullName}</td>
              <td className="px-4 py-3 text-muted-foreground">{user.login}</td>
              <td className="px-4 py-3">{ROLE_LABEL[user.role]}</td>
              <td className="px-4 py-3">
                <span
                  className={cn(
                    'inline-flex items-center rounded-full px-2 py-0.5 text-xs',
                    user.status === 'active'
                      ? 'bg-emerald-100 text-emerald-700'
                      : 'bg-muted text-muted-foreground',
                  )}
                >
                  {STATUS_LABEL[user.status]}
                </span>
              </td>
              <td className="px-4 py-3">
                {user.stores.length === 0 ? (
                  <span className="text-muted-foreground">—</span>
                ) : (
                  <span className="text-xs">{user.stores.map((s) => s.name).join(', ')}</span>
                )}
              </td>
              <td className="px-4 py-3">
                <div className="flex justify-end gap-2">
                  <Button variant="outline" size="sm" onClick={() => onEdit(user)}>
                    Редактировать
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => onResetPassword(user)}>
                    <KeyRound className="h-4 w-4" aria-hidden="true" />
                    Сбросить
                  </Button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

interface CreateUserDialogProps {
  onClose: () => void
  onSubmit: (input: CreateAdminUserInput) => void
  pending: boolean
  error: unknown
}

function CreateUserDialog({ onClose, onSubmit, pending, error }: CreateUserDialogProps) {
  const [fullName, setFullName] = useState('')
  const [login, setLogin] = useState('')
  const [role, setRole] = useState<UserRole>('seller')
  const fullNameId = useId()
  const loginId = useId()
  const roleId = useId()

  return (
    <Modal title="Новый пользователь" onClose={onClose}>
      <form
        className="flex flex-col gap-3"
        onSubmit={(e) => {
          e.preventDefault()
          onSubmit({ fullName, login, role })
        }}
      >
        <div className="flex flex-col gap-1">
          <Label htmlFor={fullNameId}>ФИО</Label>
          <Input
            id={fullNameId}
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            required
            minLength={1}
            maxLength={200}
            placeholder="Иванов Иван Иванович"
          />
        </div>
        <div className="flex flex-col gap-1">
          <Label htmlFor={loginId}>Логин</Label>
          <Input
            id={loginId}
            value={login}
            onChange={(e) => setLogin(e.target.value)}
            required
            minLength={3}
            maxLength={100}
            placeholder="ivanov"
          />
        </div>
        <div className="flex flex-col gap-1">
          <Label htmlFor={roleId}>Роль</Label>
          <select
            id={roleId}
            className="h-9 rounded-md border border-input bg-background px-3 text-sm"
            value={role}
            onChange={(e) => setRole(e.target.value as UserRole)}
          >
            <option value="seller">Продавец</option>
            <option value="coach">Тренер</option>
            <option value="admin">Админ</option>
          </select>
        </div>
        <ErrorBanner error={error} />
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" type="button" onClick={onClose}>
            Отмена
          </Button>
          <Button type="submit" disabled={pending}>
            {pending ? 'Создаём…' : 'Создать'}
          </Button>
        </div>
      </form>
    </Modal>
  )
}

interface EditUserDialogProps {
  user: AdminUser
  onClose: () => void
  onSubmit: (input: UpdateAdminUserInput) => void
  pending: boolean
  error: unknown
}

function EditUserDialog({ user, onClose, onSubmit, pending, error }: EditUserDialogProps) {
  const [fullName, setFullName] = useState(user.fullName)
  const [role, setRole] = useState<UserRole>(user.role)
  const [status, setStatus] = useState<UserStatus>(user.status)
  const fullNameId = useId()
  const roleId = useId()
  const statusId = useId()

  return (
    <Modal title={`Редактировать: ${user.fullName}`} onClose={onClose}>
      <form
        className="flex flex-col gap-3"
        onSubmit={(e) => {
          e.preventDefault()
          onSubmit({
            fullName: fullName === user.fullName ? undefined : fullName,
            role: role === user.role ? undefined : role,
            status: status === user.status ? undefined : status,
          })
        }}
      >
        <div className="flex flex-col gap-1">
          <Label htmlFor={fullNameId}>ФИО</Label>
          <Input
            id={fullNameId}
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            required
            minLength={1}
            maxLength={200}
          />
        </div>
        <div className="flex flex-col gap-1">
          <Label htmlFor={roleId}>Роль</Label>
          <select
            id={roleId}
            className="h-9 rounded-md border border-input bg-background px-3 text-sm"
            value={role}
            onChange={(e) => setRole(e.target.value as UserRole)}
          >
            <option value="seller">Продавец</option>
            <option value="coach">Тренер</option>
            <option value="admin">Админ</option>
          </select>
        </div>
        <div className="flex flex-col gap-1">
          <Label htmlFor={statusId}>Статус</Label>
          <select
            id={statusId}
            className="h-9 rounded-md border border-input bg-background px-3 text-sm"
            value={status}
            onChange={(e) => setStatus(e.target.value as UserStatus)}
          >
            <option value="active">Активен</option>
            <option value="disabled">Заблокирован</option>
          </select>
          <p className="text-xs text-muted-foreground">
            Вы не можете сами себя заблокировать или лишить роли admin.
          </p>
        </div>
        <ErrorBanner error={error} />
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" type="button" onClick={onClose}>
            Отмена
          </Button>
          <Button type="submit" disabled={pending}>
            {pending ? 'Сохраняем…' : 'Сохранить'}
          </Button>
        </div>
      </form>
    </Modal>
  )
}

interface ConfirmResetDialogProps {
  user: AdminUser
  onCancel: () => void
  onConfirm: () => void
  pending: boolean
  error: unknown
}

function ConfirmResetDialog({
  user,
  onCancel,
  onConfirm,
  pending,
  error,
}: ConfirmResetDialogProps) {
  return (
    <Modal title="Сбросить пароль?" onClose={onCancel}>
      <p className="text-sm">
        Создадим новый временный пароль для <strong>{user.fullName}</strong>. Старый пароль
        перестанет работать сразу.
      </p>
      <ErrorBanner error={error} />
      <div className="flex justify-end gap-2 pt-2">
        <Button variant="outline" type="button" onClick={onCancel}>
          Отмена
        </Button>
        <Button variant="destructive" type="button" onClick={onConfirm} disabled={pending}>
          {pending ? (
            <>
              <RefreshCw className="h-4 w-4 animate-spin" aria-hidden="true" />
              Сбрасываем…
            </>
          ) : (
            'Сбросить'
          )}
        </Button>
      </div>
    </Modal>
  )
}

interface TempPasswordDialogProps {
  title: string
  description: string
  password: string
  onClose: () => void
}

function TempPasswordDialog({ title, description, password, onClose }: TempPasswordDialogProps) {
  const [copied, setCopied] = useState(false)

  return (
    <Modal title={title} onClose={onClose}>
      <p className="text-sm">{description}</p>
      <div className="flex items-center gap-2 rounded-md border bg-muted/50 px-3 py-2 font-mono text-sm">
        <span className="flex-1 select-all">{password}</span>
        <Button
          variant="ghost"
          size="sm"
          onClick={async () => {
            await navigator.clipboard.writeText(password)
            setCopied(true)
            setTimeout(() => setCopied(false), 1500)
          }}
        >
          <Copy className="h-4 w-4" aria-hidden="true" />
          {copied ? 'Скопировано' : 'Копировать'}
        </Button>
      </div>
      <p className="text-xs text-muted-foreground">
        Пароль больше не покажется — сохраните или передайте сразу.
      </p>
      <div className="flex justify-end pt-2">
        <Button onClick={onClose}>Готово</Button>
      </div>
    </Modal>
  )
}

interface ModalProps {
  title: string
  onClose: () => void
  children: React.ReactNode
}

function Modal({ title, onClose, children }: ModalProps) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/40 px-4"
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <Card className="w-full max-w-md">
        <CardHeader className="flex-row items-start justify-between">
          <CardTitle>{title}</CardTitle>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="-mr-2 -mt-2 h-7 w-7"
            aria-label="Закрыть"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </Button>
        </CardHeader>
        <CardContent className="flex flex-col gap-3">{children}</CardContent>
      </Card>
    </div>
  )
}

function ErrorBanner({ error }: { error: unknown }) {
  if (!error) {
    return null
  }
  const message = describeErrorMessage(error, 'Не удалось выполнить действие.')
  return (
    <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
      <AlertCircle className="mt-0.5 h-4 w-4" aria-hidden="true" />
      <span>{message}</span>
    </div>
  )
}

function StatusCard({ title, description }: { title: string; description?: string }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        {description && <p className="text-sm text-muted-foreground">{description}</p>}
      </CardHeader>
    </Card>
  )
}
